import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { MetricBulkRequestCompanyWrapper } from "../../../dto/metric-bulk-request";
import { SharedComponentsModule } from "../../../../shared/module/shared-components.module";
import { ToolTipSettings } from "../../../../shared/component/tooltip/tooltip.util";
import { ModalButtons } from "../../../../shared/component/modal-container/modal-container.component";
import { KpiBusinessService } from "../../../service/backend/kpi-business.service";
import { NOTIFICATIONS, NotificationsService } from "../../../../../core/service/notifications.service";
import { MetricGroup, MetricGroupRole } from "../../../dto/MetricGroup";
import {
  CompanyMetricVisibilityDto,
  MetricGroupCompanyOrderDTO,
} from "../../../dto/CompanyMetric";
import { firstValueFrom, Subject } from "rxjs";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { MetricForm, SelectableMetricGroupForm, SelectableMetricGroupFormWrapper } from "../../../dto/MetricGroupForm";
import { MetricFillService } from "../../../service/internal/metric-fill.service";
import { MetricType } from '../../../../recovery/component/recovery-metrics-panel/entity/DefaultMetric';
import {
  ModalCreateCustomMetricGroupComponent,
} from "../../../modals/modal-custom-metric-group-crud/modal-custom-metric-group-crud.component";
import { CommonModule } from "@angular/common";
import { takeUntil, tap } from "rxjs/operators";
import { OrganizationsService } from "../../../../organization/components/service/organizations.service";
import { Organization } from "../../../../organization/dto/Organization";
import { ModalTypeAndGroupInfo } from "../../../modals/metric-group-crud-base-modal/metric-group-crud-helper.service";
import {
  TableGroupIconComponent,
} from "../../../../shared/component/icons-deprecated/table-group-icon/table-group-icon.component";
import { UUID } from "../../../../../core/pojo/id-variable";
import { MetricCalculationService } from "../../../service/internal/metric-calculation.service";

@Component({
  selector: 'app-kpi-update-metrics-visibility-bulk-request',
  standalone: true,
  imports: [
    SharedComponentsModule,
    ModalCreateCustomMetricGroupComponent,
    CommonModule,
    TableGroupIconComponent,
  ],
  templateUrl: './kpi-update-metrics-visibility-bulk-request.component.html',
  styleUrl: './kpi-update-metrics-visibility-bulk-request.component.scss',
})
export class KpiUpdateMetricsVisibilityBulkRequestComponent implements OnChanges, OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();
  protected readonly MetricGroupRole = MetricGroupRole;
  readonly enabledListId = 'enabled-list-group';
  readonly disabledListId = 'disabled-list-group';
  readonly METRIC_MINIMUM_COUNT = 3;

  @Input({ required: true }) selectedCompanyWrapper: MetricBulkRequestCompanyWrapper;
  @Input({ required: true }) allCompanyWrappers: MetricBulkRequestCompanyWrapper[];
  @Input({ required: true }) metricGroups: MetricGroup[];
  @Output() closeEmitter = new EventEmitter<boolean>();

  metricGroupForm: SelectableMetricGroupFormWrapper;
  companiesForCopy: Map<string, MetricBulkRequestCompanyWrapper>;

  toolTipSettings: ToolTipSettings = {
    show: false,
  };

  modalButtons: ModalButtons = {
    lowerLeft: 'Metric group',
    lowerLeftIcon: 'plus.svg',
    lowerLeftClass: 'accent',
    dismiss: 'Cancel',
    close: true,
    apply: 'Apply',
  };

  selectedOrg: Organization;
  isAtLeastOneGroupModified: boolean = false;
  showCreateMetricGroupModal = false;
  selectedMetricGroupForCrud: MetricGroup = new MetricGroup();

  constructor(
    private kpiService: KpiBusinessService,
    private notificationService: NotificationsService,
    private orgService: OrganizationsService,
  ) {
  }

  ngOnInit(): void {
    this.orgService.selected$
      .pipe(
        tap((org) => {
          if (!org) {
            return;
          }
          this.selectedOrg = org;
        }),
        takeUntil(this._destroyStream),
      )
      .subscribe();
  }

  ngOnChanges(): void {
    this.createFormGroup(this.metricGroups);
    this.prepareCompanyDataForCopyInput();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  close(isSuccessful: boolean): void {
    if (isSuccessful) {
      this.notificationService.success({ title: 'Changes saved', message: 'Metric groups were updated successfully' });
    }
    this.closeEmitter.emit(isSuccessful || this.isAtLeastOneGroupModified);
  }

  async confirm(): Promise<void> {
    await this.updateGroupPriority();
    await this.updateMetricVisibility();
    this.close(true);
  }

  /**
   * Copies all metrics visibility from @param companyWrapper.
   * All metrics in different companies in one org have same names.
   * Therefore, we iterate group by group and metric by metric and copy the settings.
   * @param companyWrapper
   */
  async copyFromCompany(companyWrapper: MetricBulkRequestCompanyWrapper): Promise<void> {
    if (!companyWrapper || !(companyWrapper instanceof MetricBulkRequestCompanyWrapper)) {
      return;
    }
    const otherMetricGroups = await firstValueFrom(
      this.kpiService.getMetricGroupsForCompany(this.selectedOrg.uuid, companyWrapper.company.uuid)
    );
    this.metricGroupForm.controls.groups.controls.forEach((currentGroup) => {
      const name = currentGroup.value.name;
      if (!otherMetricGroups || !otherMetricGroups.filter((group) => group.name === name).length) {
        this.notificationService.error({
          title: 'Missing group',
          message: `Could not find the group with a name ${name} from a selected company`,
        });
      }
      const groupToBeCopiedFrom = otherMetricGroups.filter((group) => group.name === name)[0];
      currentGroup.controls.priority.patchValue(groupToBeCopiedFrom.priority);
      currentGroup.controls.metrics.controls.forEach((currentMetric) => {
        const metricToBeCopiedFrom = groupToBeCopiedFrom.metrics.filter((metric) => metric.name === currentMetric.value.name)[0];
        currentMetric.controls.companyMetrics.controls[0].controls.isHidden.patchValue(metricToBeCopiedFrom.companyMetrics[0].isHidden);
      });

      this.updateMetricGroupIsSelected(currentGroup);
      this.updateVisibilityMetricsAmount(currentGroup);
    });
    this.updateMetricOrderAfterCompanyCopy();
    MetricFillService.sortGroups(this.metricGroupForm);
  }

  drop(event: CdkDragDrop<MetricGroup>): void {
    let previousIndex = event.previousIndex;
    let currentIndex = event.currentIndex;

    // disabled group starts with index 0. Since disabled group is a de-facto continuation of enabled group, we need to adjust indexes.
    const isDisabledList = event.container.id === this.disabledListId;
    if (isDisabledList) {
      const currentEnabledGroupsAmount = this.metricGroupForm.value.groups.filter((group) => group.isSelected).length;
      previousIndex += currentEnabledGroupsAmount;
      currentIndex += currentEnabledGroupsAmount;
    }

    moveItemInArray(this.metricGroupForm.controls.groups.controls, previousIndex, currentIndex);
  }

  createFormGroup(metricGroups: MetricGroup[]): void {
    this.metricGroupForm = MetricFillService.initSelectableForm(metricGroups);
  }

  /**
   * Clicking on metric group checkbox, we want to enable/disable all metrics in that metric group.
   * We have to pay attention to a couple of things:
   * 1) update all metrics visibility.
   * 2) update the metric group temp variable to show it is hidden/unhidden.
   * 3) update the metric group priority (we have enabled groups at start and then disabled groups)
   *
   * @param group to update the visibility.
   * @param value if true -> hides all metrics.
   */
  toggleMetricGroupVisibility(group: SelectableMetricGroupForm, value: boolean): void {
    group.controls.metrics.controls.forEach((metric) => {
      metric.controls.companyMetrics.controls[0].controls.isHidden.patchValue(value);
    });
    group.controls.isSelected.patchValue(!value);
    group.controls.priority.patchValue(value ? this.metricGroups.length : 0);
    MetricFillService.sortGroups(this.metricGroupForm);
    this.updateVisibilityMetricsAmount(group);
  }

  toggleMetricVisibility(group: SelectableMetricGroupForm, metric: MetricForm, value: boolean): void {
    const isGroupSelected = group.value.isSelected;
    metric.controls.companyMetrics.controls[0].controls.isHidden.patchValue(value);
    this.handleMetricSelection(group, metric);
    this.handleUnselectedMetrics(group);
    this.updateMetricGroupIsSelected(group);
    this.updateVisibilityMetricsAmount(group);
    // if the metric was last selected, then we want to update the metric group priority as well (place in disabled/enabled lists).
    if (group.value.isSelected !== isGroupSelected) {
      group.controls.priority.patchValue(value ? this.metricGroups.length : 0);
      MetricFillService.sortGroups(this.metricGroupForm);
    }
  }

  addMetricGroupClicked(metricGroupUuid?: UUID): void {
    if (metricGroupUuid) {
      this.selectedMetricGroupForCrud = this.metricGroups.filter((metricGroup) => metricGroup.uuid === metricGroupUuid)[0];
    } else {
      this.selectedMetricGroupForCrud = new MetricGroup();
    }
    this.showCreateMetricGroupModal = true;
  }

  closeNewMetricGroupModal(): void {
    this.showCreateMetricGroupModal = false;
  }

  async deleteMetricGroup(): Promise<void> {
    await firstValueFrom(this.kpiService.deleteGroup(this.selectedMetricGroupForCrud.uuid));
    const indexToDelete = this.metricGroups.indexOf(this.selectedMetricGroupForCrud);
    this.metricGroups.splice(indexToDelete, 1);
    const metricGroupFormToDelete = this.metricGroupForm.controls.groups.controls
      .filter((g) => g.value.uuid === this.selectedMetricGroupForCrud.uuid)[0];
    const indexFormToDelete = this.metricGroupForm.controls.groups.controls.indexOf(metricGroupFormToDelete);
    this.metricGroupForm.controls.groups.controls.splice(indexFormToDelete, 1);
    this.closeNewMetricGroupModal();
    MetricFillService.sortGroups(this.metricGroupForm);
  }

  async proceedMetricGroupAfterCreateOrUpdate(groupWrapper: ModalTypeAndGroupInfo): Promise<void> {
    this.showCreateMetricGroupModal = false;

    const group = groupWrapper.group as MetricGroup;
    if (groupWrapper.modalType === 'create') {
      await this.createMetricGroup(group, groupWrapper.enableForAllCompanies);
    } else {
      await this.updateMetricGroup(group);
    }

    this.isAtLeastOneGroupModified = true;
    this.notificationService.success(NOTIFICATIONS.METRICS_UPDATED_SUCCESS);
  }

  private async createMetricGroup(group: MetricGroup, isEnabledForAllCompanies: boolean): Promise<void> {
    const createdGroup = await firstValueFrom(
      this.kpiService.saveAndGetMetricGroup(group, this.selectedOrg.uuid, this.selectedCompanyWrapper.company.uuid, isEnabledForAllCompanies)
    );
    createdGroup.priority = this.metricGroupForm.value.groups.filter((_group) => _group.isSelected).length;
    this.metricGroups.push(createdGroup);
    this.metricGroupForm.controls.groups.insert(
      createdGroup.priority,
      MetricFillService.initSelectableGroupForm(createdGroup)
    );
  }

  private async updateMetricGroup(group: MetricGroup): Promise<void> {
    // update the group and its reference in the form group array original form group.
    const updatedGroup = await firstValueFrom(this.kpiService.updateMetricGroup(group, this.selectedOrg.uuid));
    this.prepareGroupAfterUpdate(updatedGroup, this.selectedCompanyWrapper.company.uuid);
    const indexToUpdate = this.metricGroups.findIndex((metricGroup) => metricGroup.uuid === updatedGroup.uuid);
    this.metricGroups[indexToUpdate] = updatedGroup;

    const formIndexToUpdate = this.metricGroupForm.value.groups.findIndex((metricGroup) => metricGroup.uuid === updatedGroup.uuid);
    const metricFormGroup = MetricFillService.initSelectableGroupForm(updatedGroup);
    metricFormGroup.controls.priority.patchValue(formIndexToUpdate);

    this.metricGroupForm.controls.groups.removeAt(formIndexToUpdate);
    this.metricGroupForm.controls.groups.insert(
      formIndexToUpdate,
      MetricFillService.initSelectableGroupForm(updatedGroup)
    );
  }

  /**
   * Prepares the group from backend, which was just updated.
   * 1) in response, the group has all company metrics, we filter only one related to our company.
   * 2) sets the temp variable for metric groups.
   *
   * @param group is updated metric group.
   * @param companyUUID to filter company metric with.
   */
  private prepareGroupAfterUpdate(group: MetricGroup, companyUUID: UUID): void {
    group.metrics.forEach((metric) => {
      metric.companyMetrics = metric.companyMetrics.filter((cm) => cm.companyUUID === companyUUID);
    });
    MetricCalculationService.setGroupVisibility(group);
    MetricCalculationService.setSignsForGroup(group);
  }

  private updateMetricOrderAfterCompanyCopy(): void {
    this.metricGroupForm.controls.groups.controls.sort((m1, m2) => m1.value.priority - m2.value.priority);
  }

  /**
   * Handles the selection and deselection of metrics within a group, ensuring proper dependencies
   * and visibility rules between **starting** and **ending** metrics, as well as group-wide visibility
   * adjustments based on selection conditions.
   *
   *
   * ### Behavior:
   * 1. For **STARTING metrics**:
   *    - If hidden, no further actions are taken.
   *    - If unhidden and a regular metric is selected, the corresponding **ENDING metric**
   *      is also unhidden.
   *
   * 2. For **ENDING metrics**:
   *    - If hidden, the corresponding **STARTING metric** is also hidden.
   *
   * 3. For group-wide conditions:
   *    - If the group has the **minimum number of metrics** (e.g., 3) and a metric is hidden,
   *      all metrics in the group are hidden.
   *    - If the group exceeds the minimum number of metrics and no regular metric is selected,
   *      all metrics are unhidden.
   *
   * ---
   *
   * @param group - The group of metrics being processed, which may have dependencies
   *                between metrics and group-wide rules.
   * @param metric - The specific metric being selected or deselected.
   */
  private handleMetricSelection(group: SelectableMetricGroupForm, metric: MetricForm): void {
    const type = metric.value.props.type;
    const isHidden = metric.value.companyMetrics[0].isHidden;
    const hasMinimumMetrics = group.value.metrics.length === this.METRIC_MINIMUM_COUNT;
    const exceedsMinimumMetrics = group.value.metrics.length >= this.METRIC_MINIMUM_COUNT;
    const someRegularMetricSelected = this.isSomeRegularMetricSelected(group);

    // Handle 'STARTING' metric selection
    if (type === MetricType.STARTING) {
      if (isHidden) return;

      if (!isHidden && someRegularMetricSelected) {
        const ending = this.findStartingOrEnding(group, false);
        if (ending) {
          ending.controls.companyMetrics.controls[0].controls.isHidden.patchValue(false);
        }
      }
    }

    // Handle 'ENDING' metric selection
    if (type === MetricType.ENDING && isHidden) {
      const starting = this.findStartingOrEnding(group, true);
      if (starting) {
        starting.controls.companyMetrics.controls[0].controls.isHidden.patchValue(true);
      }
    }

    // Handle hiding/showing metrics based on group conditions
    if (hasMinimumMetrics && isHidden) {
      this.setIsHiddenForAllMetrics(group, true);
    } else if (exceedsMinimumMetrics && !isHidden && !someRegularMetricSelected) {
      this.setIsHiddenForAllMetrics(group, false);
    }
  }

  private prepareCompanyDataForCopyInput(): void {
    const map = new Map<string, MetricBulkRequestCompanyWrapper>();
    this.allCompanyWrappers.forEach((wrapper) => {
      map.set(wrapper.company.name, wrapper);
    });
    this.companiesForCopy = map;
  }

  private async updateGroupPriority(): Promise<void> {
    this.metricGroupForm.controls.groups.controls.forEach((group, i) => {
      group.controls.priority.patchValue(i);
    });
    const dtos: MetricGroupCompanyOrderDTO[] = this.metricGroupForm.value.groups.map((group) => ({
      metricGroupUUID: group.uuid,
      priority: group.priority,
    }));
    await firstValueFrom(this.kpiService.updateMetricGroupsOrder(dtos, this.selectedCompanyWrapper.company.uuid));
  }

  private async updateMetricVisibility(): Promise<void> {
    const dtos: CompanyMetricVisibilityDto[] = this.metricGroupForm.value.groups
      .flatMap((group) => group.metrics)
      .map((metric) => {
        return {
          uuid: metric.companyMetrics[0].uuid,
          isHidden: metric.companyMetrics[0].isHidden,
        };
      });
    await firstValueFrom(this.kpiService.updateMetricGroupsVisibility(dtos, this.selectedCompanyWrapper.company.uuid));
  }

  private updateVisibilityMetricsAmount(group: SelectableMetricGroupForm): void {
    group.controls.visibleMetricsAmount.patchValue(group.value.metrics.filter((m) => !m.companyMetrics[0].isHidden).length);
  }

  private updateMetricGroupIsSelected(group: SelectableMetricGroupForm): void {
    group.controls.isSelected.patchValue(group.value.metrics.some((m) => !m.companyMetrics[0].isHidden));
  }

  private setIsHiddenForAllMetrics(group: SelectableMetricGroupForm, isHidden: boolean): void {
    group.controls.metrics.controls.forEach((metric) => metric.controls.companyMetrics.controls[0].controls.isHidden.patchValue(isHidden));
  }

  private handleUnselectedMetrics(group: SelectableMetricGroupForm): void {
    const regularMetricsSelectedCount = group.value.metrics.filter((m) =>
      m.props.type === MetricType.REGULAR && !m.companyMetrics[0].isHidden).length;
    if (!regularMetricsSelectedCount) {
      group.controls.metrics.controls
        .filter((m) => m.controls.props.value.type !== MetricType.REGULAR)
        .forEach((m) => m.controls.companyMetrics.controls[0].controls.isHidden.patchValue(true));
    }
  }

  private isSomeRegularMetricSelected(group: SelectableMetricGroupForm): boolean {
    return group.controls.metrics.value.some((m) =>
      m.props.type === MetricType.REGULAR && !m.companyMetrics[0].isHidden);
  }

  private findStartingOrEnding(group: SelectableMetricGroupForm, isStarting: boolean): MetricForm {
    const metricType = isStarting ? MetricType.STARTING : MetricType.ENDING;
    const metrics = group.controls.metrics.controls.filter((m) => m.controls.props.value.type === metricType);
    return metrics.length ? metrics[0] : null;
  }
}
